<template>
  <div style="height: 80px;">
    <DesktopNavbar v-if="state.isDesktop" :is-scrolled="state.isScrolled" :grouped-services="state.groupedServices"
      :specialist-services="state.specialistServices" @home-scroll="homeScroll" />
    <MobileNavbar v-else :is-scrolled="state.isScrolled" :home-services="state.homeServices"
      :specialist-services="state.specialistServices" @home-scroll="homeScroll" />
  </div>
</template>

<script setup lang="ts">
import { reactive, onMounted, onUnmounted } from 'vue';
import { useApolloClient } from '@vue/apollo-composable';
import { getServiceList, getSpecialistServiceList } from "@/utils/graphql";
import { scrollTop } from "@/utils/functions";
import DesktopNavbar from './DesktopNavbar.vue';
import MobileNavbar from './MobileNavbar.vue';

interface Service {
  displayText: string;
  path: string;
  serviceType: {
    contentItems: {
      displayText: string;
    }[];
  };
  showOnHomePage?: boolean;
}

const { resolveClient } = useApolloClient();

const state = reactive({
  isScrolled: false,
  isDesktop: window.innerWidth >= 992,
  homeServices: [] as any[],
  groupedServices: {} as Record<string, any[]>,
  specialistServices: [] as any[],
});

const groupServicesByType = (services: Service[]) => {
  const grouped: Record<string, Service[]> = {};
  if (!services) return grouped;
  services.forEach(service => {
    if (service.serviceType?.contentItems) {
      service.serviceType.contentItems.forEach(type => {
        const typeName = type.displayText;
        if (!grouped[typeName]) {
          grouped[typeName] = [];
        }
        grouped[typeName].push(service);
      });
    }
  });
  return grouped;
};

const handleScroll = () => {
  state.isScrolled = window.scrollY > 50;
};

const handleResize = () => {
  state.isDesktop = window.innerWidth >= 992;
};

const homeScroll = () => {
  if (window.scrollY !== 0) {
    setTimeout(() => scrollTop(''), 50);
  }
};

onMounted(async () => {
  window.addEventListener('resize', handleResize);
  window.addEventListener('scroll', handleScroll);

  try {
    const ApolloClient = resolveClient();
    const [servicesData, specialistServicesData] = await Promise.all([
      getServiceList(ApolloClient),
      getSpecialistServiceList(ApolloClient)
    ]);

    if (servicesData) {
      state.homeServices = servicesData;
      state.groupedServices = groupServicesByType(servicesData);
    }

    if (specialistServicesData) {
      state.specialistServices = specialistServicesData;
    }

  } catch (error) {
    console.error("Failed to load navigation data:", error);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

/* --- SHARED STYLES FOR BOTH DESKTOP & MOBILE --- */

.navbar {
  width: 100%;
  z-index: 1000;
  transition: background 0.3s ease-in-out, backdrop-filter 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
  position: fixed !important;
  top: 0;
  left: 0;
  right: 0;
  background-color: $nls-light;
}

.navbar.scrolled {
  background: rgba(255, 255, 255, 0.8);
  -webkit-backdrop-filter: blur(12px);
  backdrop-filter: blur(12px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1), 0 5px 15px rgba(0, 0, 0, 0.1);
}

.navbar .navbar-brand,
.navbar .nav-link,
.navbar .btn {
  filter: none;
}

.nav-link {
  margin: 0 0.5rem;
  transition: 0.25s;
  &:hover {
    transition: 0.25s;
  }
}

/* --- SHARED CONTAINER WIDTHS --- */
@media (min-width: 500px) {

  .container-sm,
  .container {
    max-width: 97%;
  }
}

@media (min-width: 768px) {

  .container-md,
  .container-sm,
  .container {
    max-width: 95%;
  }
}

@media (min-width: 992px) {

  .container-lg,
  .container-md,
  .container-sm,
  .container {
    max-width: 95%;
  }
}

@media (min-width: 1200px) {

  .container-xl,
  .container-lg,
  .container-md,
  .container-sm,
  .container {
    max-width: 95%;
  }
}

@media (min-width: 1400px) {

  .container-xxl,
  .container-xl,
  .container-lg,
  .container-md,
  .container-sm,
  .container {
    max-width: 1420px;
  }
}
</style>